// src/components/common/DocumentGallery.jsx
import React, { useState } from 'react';
import DocumentViewer from '../attestations/DocumentViewer';
import ConfirmDialog from './ConfirmDialog';

const DocumentGallery = ({ documents = [], entityId, entityType, onDelete, onView }) => {
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showViewer, setShowViewer] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  const styles = {
    container: {
      marginTop: '1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
    },
    title: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
      margin: 0,
    },
    viewToggle: {
      display: 'flex',
      gap: '0.25rem',
      padding: '0.25rem',
      backgroundColor: '#f3f4f6',
      borderRadius: '0.375rem',
    },
    viewToggleButton: {
      padding: '0.375rem 0.75rem',
      background: 'none',
      border: 'none',
      borderRadius: '0.25rem',
      fontSize: '0.875rem',
      cursor: 'pointer',
      color: '#6b7280',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      transition: 'all 0.2s',
    },
    viewToggleButtonActive: {
      backgroundColor: '#ffffff',
      color: '#111827',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '1rem',
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    card: {
      padding: '1rem',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cardHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      borderColor: '#2563eb',
    },
    gridCard: {
      display: 'flex',
      flexDirection: 'column',
    },
    listCard: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    icon: {
      fontSize: '2rem',
      marginBottom: '0.5rem',
      textAlign: 'center',
    },
    listIcon: {
      fontSize: '1.5rem',
      minWidth: '40px',
      textAlign: 'center',
    },
    info: {
      flex: 1,
    },
    name: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
      marginBottom: '0.25rem',
      wordBreak: 'break-word',
    },
    meta: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    type: {
      display: 'inline-block',
      padding: '0.125rem 0.375rem',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      borderRadius: '0.25rem',
      fontSize: '0.625rem',
      fontWeight: 500,
      textTransform: 'uppercase',
    },
    actions: {
      display: 'flex',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
    listActions: {
      display: 'flex',
      gap: '0.5rem',
    },
    actionButton: {
      padding: '0.25rem',
      background: 'none',
      border: 'none',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      color: '#6b7280',
      transition: 'all 0.2s',
    },
    deleteButton: {
      color: '#dc2626',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      border: '2px dashed #e5e7eb',
      color: '#6b7280',
    },
  };

  const getFileIcon = (fileType, fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    
    if (fileType?.startsWith('image/')) return '🖼️';
    if (fileType?.includes('pdf') || extension === 'pdf') return '📄';
    if (fileType?.includes('word') || ['doc', 'docx'].includes(extension)) return '📝';
    if (fileType?.includes('excel') || ['xls', 'xlsx'].includes(extension)) return '📊';
    if (fileType?.includes('powerpoint') || ['ppt', 'pptx'].includes(extension)) return '📽️';
    if (fileType?.includes('text') || extension === 'txt') return '📃';
    if (['zip', 'rar', '7z'].includes(extension)) return '🗜️';
    if (['mp3', 'wav', 'ogg'].includes(extension)) return '🎵';
    if (['mp4', 'avi', 'mov'].includes(extension)) return '🎬';
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDocumentClick = (doc) => {
    setSelectedDocument(doc);
    setShowViewer(true);
    if (onView) onView(doc);
  };

  const handleDeleteClick = (doc, e) => {
    e.stopPropagation();
    setDocumentToDelete(doc);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (onDelete) {
      onDelete(documentToDelete);
    }
    setShowDeleteConfirm(false);
    setDocumentToDelete(null);
  };

  if (!documents || documents.length === 0) {
    return (
      <div style={styles.emptyState}>
        Aucun document téléchargé
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Documents ({documents.length})</h3>
        <div style={styles.viewToggle}>
          <button
            style={{
              ...styles.viewToggleButton,
              ...(viewMode === 'grid' ? styles.viewToggleButtonActive : {}),
            }}
            onClick={() => setViewMode('grid')}
          >
            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            Grille
          </button>
          <button
            style={{
              ...styles.viewToggleButton,
              ...(viewMode === 'list' ? styles.viewToggleButtonActive : {}),
            }}
            onClick={() => setViewMode('list')}
          >
            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            Liste
          </button>
        </div>
      </div>

      <div style={viewMode === 'grid' ? styles.grid : styles.list}>
        {documents.map((doc) => (
          <div
            key={doc.id}
            style={{
              ...styles.card,
              ...(viewMode === 'grid' ? styles.gridCard : styles.listCard),
            }}
            onClick={() => handleDocumentClick(doc)}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, styles.cardHover);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}
          >
            <div style={viewMode === 'grid' ? styles.icon : styles.listIcon}>
              {getFileIcon(doc.fileType, doc.name)}
            </div>
            <div style={styles.info}>
              <div style={styles.name}>{doc.originalName || doc.name}</div>
              <div style={styles.meta}>
                {formatFileSize(doc.fileSize)} • {new Date(doc.createdAt).toLocaleDateString()}
              </div>
              <div style={styles.type}>
                {doc.fileType?.split('/')[1] || doc.name?.split('.').pop()}
              </div>
            </div>
            <div style={viewMode === 'grid' ? styles.actions : styles.listActions}>
              <button
                style={styles.actionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(doc.storagePath, '_blank');
                }}
              >
                <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>
              <button
                style={{ ...styles.actionButton, ...styles.deleteButton }}
                onClick={(e) => handleDeleteClick(doc, e)}
              >
                <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      {showViewer && (
        <DocumentViewer
          document={selectedDocument}
          attestationId={entityId}
          onClose={() => {
            setShowViewer(false);
            setSelectedDocument(null);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDocumentToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Supprimer le document"
        message={`Êtes-vous sûr de vouloir supprimer le document "${documentToDelete?.originalName || documentToDelete?.name}" ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default DocumentGallery;